import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import '../../modules/module.css';
import './maintenanceQueue.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useHotkeys } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { ViewToggle } from '../../../components/ViewToggle';
import { useNavActions } from '../../../hooks/useNavActions';
import { fetchQueue, type QueueRow } from './api';
import MaintenanceRecordPage from './MaintenanceRecordPage';

// ─────────────────────────────────────────────────────────────────────────────
// Instrument Management ▸ Maintenance Queue (nav `instm-maint-queue`).
// Port of legacy ALPACA `MaintenanceTaskCheckList_ListView_MaintenanceQueue`. The list is the shared module list
// (server criteria in ModuleRegistry — one row per instrument and frequency, role / division scoped); this page adds the
// green Start icon on each row (G5 `MaintenanceRecord` action) which REPLACES the list with the Maintenance Record
// detail (standing rule 12). The list is never a record view on its own: List → Start → Record is the only flow.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Maintenance Queue';

type Screen = { kind: 'list' } | { kind: 'record'; oid: string; onlyOids?: string[]; key: number };

const COLUMNS: AppColumn<QueueRow>[] = [
  { field: 'maintenanceId', header: 'MaintenanceId', width: 140 },
  { field: 'dueDate', header: 'DueDate', width: 110, type: 'date' },
  { field: 'instrumentName', header: 'Instrument Name', width: 200 },
  { field: 'frequency', header: 'Frequency', width: 110 },
  { field: 'tasks', header: 'Tasks', width: 240 },
  { field: 'taskCount', header: 'TaskCount', width: 100, align: 'right' },
  { field: 'daysPastDue', header: 'DaysPastDue', width: 110, align: 'right' },
  { field: 'ownership', header: 'Ownership', width: 160 },
  { field: 'status', header: 'Status', width: 110 },
];

export default function MaintenanceQueuePage({ onHome, pageId = 'instm-maint-queue' }: { onHome?: () => void; pageId?: string }) {
  const notify = useNotify();
  const perm = useNavActions(pageId);
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [screen, setScreen] = useState<Screen>({ kind: 'list' });
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      setRows(await fetchQueue());
      // A reload can drop rows; a surviving selection would point at nothing.
      setSelected(new Set());
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the maintenance queue.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.maintenanceId, r.instrumentName, r.frequency, r.tasks, r.ownership, r.status]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  const start = (oid: string, onlyOids?: string[]) => setScreen({ kind: 'record', oid, onlyOids, key: Date.now() });
  /** The single ticked row — Start needs exactly one (G5 RequireSingleObject). */
  const one = selected.size === 1 ? String([...selected][0]) : null;
  useHotkeys([{ combo: 'alt+o', label: 'Start Maintenance Record', group: 'Maintenance Queue', order: 1,
    disabled: !one || screen.kind !== 'list', run: () => { if (one) start(one); } }]);

  if (screen.kind === 'record') {
    return (
      <MaintenanceRecordPage
        key={screen.key}
        oid={screen.oid}
        onlyOids={screen.onlyOids}
        canWrite={perm.write}
        siblingIds={shown.map((r) => r.oid)}
        onNavigate={(next) => start(next)}
        onHome={onHome ?? (() => undefined)}
        onClose={() => { setScreen({ kind: 'list' }); void load(); }}
        onOpen={start}
      />
    );
  }

  // The green Start icon ahead of the data columns — opens the Maintenance Record (G5 MaintenanceRecord action).
  const columns: AppColumn<QueueRow>[] = [
    { field: '__start', header: '', width: 40, sortable: false, filterable: false, noExport: true,
      render: (r) => (
        <button type="button" className="mlv-rowact mq-start" title="Start Maintenance Record" aria-label={`Start maintenance record ${r.maintenanceId}`}
                onClick={(e) => { e.stopPropagation(); start(r.oid); }}>
          <i className="ti ti-player-play-filled" aria-hidden="true" />
        </button>
      ) },
    ...COLUMNS,
  ];

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        {/* The standard list shell: breadcrumb, title, toolbar, search row, then the grid card. */}
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Instrument Management</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Maintenance Queue</span>
        </div>
        {/* List View / Detail View, as every list carries it: Detail opens what G5 opens on a row — the Maintenance Record of the
            ticked row, or of the first row when none is ticked. */}
        <div style={{ padding: '2px 0 8px' }}>
          <ViewToggle view="list" detailDisabled={shown.length === 0}
                      onChange={(v) => { if (v !== 'detail') return; const id = one ?? shown[0]?.oid; if (id) start(id); }} />
        </div>
        <FormTitle pageId={pageId} loadMs={loadMs} loading={loading} auditFormName={FORM_NAME} />
        <AppToolbar
          onRefresh={() => load()}
          refreshing={loading}
          // G5's bar is New · Refresh · ▷ (Start) · Export to · Search — Start sits between Refresh and Export to, and
          // opens the ONE ticked row (the same as the green icon on the row).
          beforeExport={
            <button type="button" className="ca-btn" disabled={!one} onClick={() => one && start(one)}
                    title="Start Maintenance Record (Alt+O)" aria-label="Start Maintenance Record">
              <span className="tb-i tb-i-green"><i className="ti ti-player-play-filled" aria-hidden="true" /></span> Start
            </button>
          }
          exportData={{ filename: 'maintenance-queue', ...gridExportMatrix(shown, COLUMNS) }}
        />
        <GridSearch value={search} onChange={setSearch} placeholder="Text to search..." />
        <AppDataGrid<QueueRow>
          rows={shown}
          columns={columns}
          getRowId={(r) => r.oid}
          loading={loading}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          onRowDoubleClick={(r) => start(r.oid)}
          defaultPageSize={20}
          emptyText={search ? 'No results match your search.' : 'No data to display'}
        />
      </div>
    </div>
  );
}
